import { z } from 'zod';
import { MoneySchema } from './common.js';
import { VatCreditStatusSchema } from './doc-ingest.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in ar.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed counterpart — for net/derived figures that can legitimately go negative
// (a credit-note rollup, an over-paid bill's outstanding). MoneySchema rejects
// negative strings (it requires d.gte(0)), so the negative wire value lands on
// the z.coerce.number() branch. Same inferred type (string | number) as DecimalLike.
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// ApBillStatus enum
// ============================================================
export const ApBillStatusSchema = z.enum(['PENDING', 'POSTED', 'PAID', 'VOID']);
// ============================================================
// Engine-approval (M1b-ii approve-by-exception) — the chain row mirrors
// ap_bill_approvals. Surfaced on the detail so the web ApprovalTimeline can
// render the maker → reviewer(s) → approver chain. approvalStatus is the
// ENGINE state (DRAFT/PENDING/APPROVED/REJECTED/RETURNED), kept SEPARATE from
// the accounting `status` (DECISION-Z).
// ============================================================
export const ApBillApprovalRowSchema = z.object({
    id: z.uuid(),
    stepNo: z.number().int().positive(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
});
// ============================================================
// ApBillLine — child rows of an ApBill
// ============================================================
export const ApBillLineSchema = z.object({
    id: z.uuid(),
    billId: z.uuid(),
    lineNo: z.number().int().positive(),
    description: z.string().min(1),
    qty: DecimalLike,
    unitPrice: DecimalLike,
    amount: DecimalLike,
    expenseCode: z.string().min(1),
});
// Wire shape — id/billId/lineNo/amount are server-derived.
export const CreateApBillLineSchema = z.object({
    description: z.string().min(1),
    qty: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    expenseCode: z.string().min(1),
});
// ============================================================
// ApBill — header
// ============================================================
// FX read-shape mirroring ar.ts — Prisma Decimal columns serialize as strings,
// so accept either string or number; nullable for THB rows.
const ForeignAmountLike = z.union([MoneySchema, z.coerce.number()]).nullable().optional();
export const ApBillSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    billDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    vendorInvoiceNo: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    subtotal: DecimalLike,
    vatRate: DecimalLike,
    vatAmount: DecimalLike,
    totalAmount: DecimalLike,
    paidAmount: DecimalLike,
    // AP Phase C2 — signed running total of POSTED credit/debit notes booked
    // against this bill (CREDIT subtracts, DEBIT adds). Outstanding =
    // totalAmount + noteAdjustment − paidAmount. Defaults to 0 (no notes), so
    // existing list/KPI math is unchanged for bills that have no notes.
    noteAdjustment: SignedDecimalLike.default(0),
    // AP Phase C1 — payment-term + early-payment-discount snapshot (defaults 0 ⇒
    // no discount, pre-C1 behaviour). paymentTermCode is the resolved term's code.
    paymentTermCode: z.string().nullable().optional(),
    discountPercent: DecimalLike.default(0),
    discountDays: z.coerce.number().int().default(0),
    // FX — defaults to THB; foreignAmount + exchangeRateAtBooking are null for
    // THB rows, populated for foreign currency rows.
    currencyCode: z.string().default('THB'),
    foreignAmount: ForeignAmountLike,
    exchangeRateAtBooking: ForeignAmountLike,
    status: ApBillStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Engine-approval columns (M1b-ii). Null/0 for manual bills.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    // B3: deferred input-VAT (SUSPENSE → CLAIMABLE) columns.
    vatCreditStatus: VatCreditStatusSchema.nullable().optional(),
    vatRealizedAt: z.coerce.date().nullable().optional(),
    vatRealizationJvId: z.uuid().nullable().optional(),
    taxInvoiceNo: z.string().nullable().optional(),
    taxInvoiceDate: z.coerce.date().nullable().optional(),
    originalReceivedAt: z.coerce.date().nullable().optional(),
    // M5b true-up — header-level link to an op-contract's standing monthly accrual
    // (set pre-post on a PENDING bill). Null for normal, unlinked bills.
    opContractId: z.uuid().nullable().optional(),
    opContractAccrualId: z.uuid().nullable().optional(),
});
// List-row shape — adds the derived agingDays + display names for table
// rendering. agingDays is computed server-side relative to today.
export const ApBillListRowSchema = ApBillSchema.extend({
    agingDays: z.number().int(),
    vendorName: z.string(),
    branchName: z.string().nullable().optional(),
});
// BT-7 — payments + their 50-ทวิ certificates, surfaced on the bill detail so
// the AP drawer can print each cert inline. Grouped by payment voucher; each
// cert carries `channel` so the UI hides print + shows "ออกโดยธนาคาร (e-WHT)"
// for bank-issued (E_WHT) certs (mirrors the /wht cert-list behavior).
// `channel` uses the same literal set as WhtChannelSchema (declared further
// below); inlined here to keep this block above the ApBill detail schema.
export const ApBill50TawiCertSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    incomeType: z.string(),
    amount: DecimalLike,
    channel: z.enum(['NORMAL', 'E_WHT']),
});
export const ApBillPayment50TawiSchema = z.object({
    paymentId: z.uuid(),
    paymentVoucherNo: z.string(),
    paymentDate: z.coerce.date(),
    certs: z.array(ApBill50TawiCertSchema),
});
// AP Phase C2 — lean note row surfaced on the bill detail so the AP drawer can
// list the bill's credit/debit notes (voucher, type, total, status) without
// pulling each note's lines. The full note detail is fetched via the notes
// endpoint when the approval panel opens. Declared inline (not via the header
// ApCreditDebitNoteSchema below) so this schema stays above that block.
export const ApBillNoteSummarySchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    noteType: z.enum(['CREDIT', 'DEBIT']),
    noteDate: z.coerce.date(),
    totalAmount: DecimalLike,
    status: z.enum(['DRAFT', 'POSTED', 'VOID']),
    approvalStatus: z.string().nullable().optional(),
});
// Detail shape — includes lines + allocations + display names.
export const ApBillWithLinesSchema = ApBillSchema.extend({
    lines: z.array(ApBillLineSchema),
    approvals: z.array(ApBillApprovalRowSchema).default([]),
    vendorName: z.string(),
    branchName: z.string().nullable().optional(),
    agingDays: z.number().int(),
    // BT-7 — optional so existing consumers (and bills with no payments yet)
    // don't break; defaults to [] when absent.
    payments50tawi: z.array(ApBillPayment50TawiSchema).optional().default([]),
    // AP Phase C2 — the canonical note-adjusted outstanding (string, 2dp) the
    // service computes in findBill, plus the bill's credit/debit notes for the
    // drawer's "ใบลด/เพิ่มหนี้" section. Both optional so older consumers/tests
    // that build a lean detail fixture don't break.
    outstanding: SignedDecimalLike.optional(),
    creditDebitNotes: z.array(ApBillNoteSummarySchema).optional().default([]),
    // AP Phase C1 — derived early-payment-discount window surfaced on the detail
    // so the RecordPaymentModal can show the discount checkbox. All optional:
    // discountDueDate = billDate + discountDays; discountAmount = subtotal ×
    // discountPercent/100; inDiscountWindow = today ≤ discountDueDate AND
    // discountPercent > 0. Absent / null when the bill carries no discount.
    discountDueDate: z.coerce.date().nullable().optional(),
    discountAmount: DecimalLike.optional(),
    inDiscountWindow: z.boolean().optional(),
});
// Wire shape for POST — voucherNo / subtotal / vatAmount / totalAmount are all
// generated server-side. Dates are wire-as-ISO per Phase 3b convention.
export const CreateApBillSchema = z
    .object({
    billDate: z.iso.date(),
    dueDate: z.iso.date(),
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    vendorInvoiceNo: z.string().optional(),
    description: z.string().optional(),
    vatRate: z.coerce.number().min(0).max(100).default(7),
    // If false, the bill is created in PENDING with no JV (awaiting approval).
    // Default true mirrors AR behaviour where invoices post immediately.
    postJv: z.coerce.boolean().default(true),
    // FX — currencyCode defaults to THB at the service layer (kept optional so
    // existing clients don't need to change). When currencyCode != THB, both
    // foreignAmount + exchangeRateAtBooking must be populated > 0 (validated in
    // ApBillsService.createBill).
    currencyCode: z.string().optional(),
    foreignAmount: z.coerce.number().positive().optional(),
    exchangeRateAtBooking: z.coerce.number().positive().optional(),
    // AP Phase C1 — optional payment-term preset code. When supplied (or the
    // vendor's defaultPaymentTermCode resolves) the service derives
    // dueDate = billDate + term.netDays and snapshots the discount window onto the
    // bill. Omitted ⇒ the caller-supplied dueDate is kept (pre-C1 behaviour).
    paymentTermCode: z.string().optional(),
    // CIP Phase 4b — optional link to a CIP project (งานระหว่างก่อสร้าง). When
    // set, the service validates the project is ACTIVE in the company and routes
    // the bill's work-cost debit to the CIP_CLEARING holding account instead of
    // each line's expenseCode (the VAT_INPUT + Cr AP_TRADE legs are unchanged).
    // The CIP page's "กล่องรับเข้า (AP)" inbox then capitalizes the clearing line
    // into CIP_ASSET + a CipCostEntry. Omitted ⇒ normal expense-account routing.
    cipProjectId: z.uuid().optional(),
    // M7 Phase 1a (E) — HONEST-COLLISION ESCAPE. The default path (dupSeq omitted /
    // 0, no reason) is unchanged: a manual bill that re-derives the SAME logical
    // key as an existing one is 409-blocked by the rebuilt partial-unique dedup
    // index. When an operator GENUINELY needs to record two identical physical
    // invoices from one vendor (same taxId + invoiceNo + total), they re-key the
    // 2nd with dupSeq=1 (+ subsequent collisions 2, 3, …) AND a mandatory audit
    // reason — dupSeq is stamped into the dedup columns so it no longer collides
    // with the dupSeq=0 original. dupSeq>0 with NO reason is rejected (the override
    // is only legitimate WITH an audit trail). The .refine below enforces that pairing.
    dupSeq: z.coerce.number().int().min(0).optional(),
    dupOverrideReason: z.string().trim().min(1).max(500).optional(),
    lines: z.array(CreateApBillLineSchema).min(1),
})
    .refine((val) => (val.dupSeq ?? 0) === 0 || !!val.dupOverrideReason, {
    path: ['dupOverrideReason'],
    message: 'dupOverrideReason is required when dupSeq > 0 (intentional duplicate needs an audit reason)',
});
// Wire shape for POST /:id/reject — captures the rejection reason which is
// stashed into the bill's description field (we re-use VOID status as the
// "rejected before posting" terminal state to avoid a schema.prisma migration
// for a brand-new REJECTED enum value — see ap-bills.service.rejectBill()).
export const RejectApBillSchema = z.object({
    reason: z.string().min(1).max(500),
});
// Wire shape for POST /:id/link-opcontract (M5b true-up). Links a PENDING AP bill
// to an op-contract's POSTED standing accrual so the bill's JV clears the accrual
// (Dr ACCRUED_EXPENSE) and books only the variance. Both ids required; the service
// validates the bill is PENDING + the accrual is POSTED, same company, unlinked.
export const LinkOpContractSchema = z.object({
    opContractId: z.uuid(),
    opContractAccrualId: z.uuid(),
});
// ============================================================
// ApPayment + allocations
// ============================================================
export const ApPaymentAllocationSchema = z.object({
    id: z.uuid(),
    paymentId: z.uuid(),
    billId: z.uuid(),
    amount: DecimalLike,
});
export const CreateApPaymentAllocationSchema = z.object({
    billId: z.uuid(),
    amount: z.coerce.number().positive(),
    // AP Phase C1 — take the bill's early-payment (cash) discount on THIS
    // allocation. Eligible only when the allocation settles the bill's FULL
    // outstanding within the discount window (paymentDate ≤ billDate+discountDays
    // AND discountPercent > 0). A payment-level `takeDiscount` (below) is the
    // default for every allocation that doesn't set its own. Omitted ⇒ no discount.
    takeDiscount: z.coerce.boolean().optional(),
});
export const ApPaymentSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    paymentDate: z.coerce.date(),
    vendorId: z.uuid(),
    cashAccountCode: z.string().min(1),
    totalAmount: DecimalLike,
    description: z.string().nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    // M7 Phase 1b (B) — single-bill payment engine 4-eyes (docType APV; DECISION-Z).
    // There is NO accounting status enum on ApPayment; "live" == journalEntryId IS
    // NOT NULL. These mirror ApPaymentRun so the read shape feeds the same engine UI.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    // M7 Phase 2 (D5) — destination-bank SNAPSHOT, frozen from the vendor's CURRENT
    // bank at DRAFT create. Pay/print/bank-file MUST read THIS snapshot, never the
    // live Vendor record, so a later VBC bank change cannot redirect an in-flight
    // payment. Surfaced read-only on the payment detail (panel).
    destBankAccountNumber: z.string().nullable().optional(),
    destBankCode: z.string().nullable().optional(),
    destBankAccountName: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const ApPaymentWithAllocationsSchema = ApPaymentSchema.extend({
    allocations: z.array(ApPaymentAllocationSchema),
    vendorName: z.string(),
});
// M7 Phase 1b — the findPayment read shape: allocations + the materialized
// engine approval chain (re-materialized on resubmit). Mirrors
// ApPaymentRunWithDetailSchema so the single-bill payment 4-eyes panel renders
// the maker → reviewer(s) → approver timeline.
export const ApPaymentWithDetailSchema = ApPaymentWithAllocationsSchema.extend({
    approvals: z.array(ApBillApprovalRowSchema).default([]),
});
// ============================================================
// WHT-at-payment (B1) — channel + per-(bill,income-type) lines
// ============================================================
export const WhtChannelSchema = z.enum(['NORMAL', 'E_WHT']);
// One WHT instruction line on a payment. The server maps the bill's expense
// lines → income-type via WhtExpenseMap when incomeType is omitted; a supplied
// incomeType/rate overrides. rate omitted ⇒ server resolves from WhtIncomeType.
export const WhtPaymentLineInputSchema = z.object({
    billId: z.uuid(),
    incomeType: z.string().min(1),
    rate: z.coerce.number().min(0).max(100).optional(),
});
// Persisted WHT line (read shape) — server-computed base/amount.
export const WhtPaymentLineSchema = z.object({
    id: z.uuid(),
    paymentId: z.uuid(),
    billId: z.uuid().nullable().optional(),
    incomeType: z.string(),
    base: DecimalLike,
    rate: DecimalLike,
    amount: DecimalLike,
    whtCertificateId: z.uuid().nullable().optional(),
});
export const CreateApPaymentSchema = z.object({
    paymentDate: z.iso.date(),
    vendorId: z.uuid(),
    cashAccountCode: z.string().min(1),
    description: z.string().optional(),
    allocations: z.array(CreateApPaymentAllocationSchema).min(1),
    // B1: per-(bill,income-type) WHT. Omitted ⇒ no WHT (backward compatible).
    whtLines: z.array(WhtPaymentLineInputSchema).optional(),
    // B5: payment channel. Defaults to NORMAL at the service layer.
    whtChannel: WhtChannelSchema.optional(),
    paymentMethod: z.string().optional(), // TRANSFER | CHEQUE | CASH
    // M7 Phase 1a (A) — REQUIRED hard dedup guard. A keyless retry / double-click is
    // the single biggest double-pay vector; the body key (with the @unique column +
    // P2002 catch) closes it. Body wins over the legacy Idempotency-Key header.
    idempotencyKey: z.string().min(1),
    // AP Phase C1 — payment-level default for taking each bill's early-payment
    // discount. Applies to every allocation that doesn't set its own takeDiscount.
    // The service still gates each on the per-bill window + full-settlement
    // eligibility; an in-eligible allocation simply takes no discount.
    takeDiscount: z.coerce.boolean().optional(),
});
// Extend the read shape so the web sees whtTotal/netPaid/whtChannel.
export const ApPaymentReadExtraSchema = z.object({
    whtTotal: DecimalLike.optional(),
    netPaid: DecimalLike.optional(),
    whtChannel: WhtChannelSchema.optional(),
    paymentMethod: z.string().nullable().optional(),
    // AP Phase C1 — early-payment discount captured on this payment (defaults 0).
    discountTotal: DecimalLike.optional(),
    // M7 Phase 2 (D5) — destination-bank snapshot (also on ApPaymentSchema base).
    destBankAccountNumber: z.string().nullable().optional(),
    destBankCode: z.string().nullable().optional(),
    destBankAccountName: z.string().nullable().optional(),
});
// ============================================================
// Cross-vendor payment-run (B2) — dual-status, engine-approved
// ============================================================
export const ApPaymentRunStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'VOID',
]);
export const CreateApPaymentRunAllocationSchema = z.object({
    billId: z.uuid(),
    vendorId: z.uuid(),
    amount: z.coerce.number().positive(),
    whtIncomeType: z.string().min(1).optional(),
    whtRate: z.coerce.number().min(0).max(100).optional(),
});
export const CreateApPaymentRunSchema = z.object({
    paymentDate: z.iso.date(),
    cashAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    note: z.string().optional(),
    whtChannel: WhtChannelSchema.optional(),
    allocations: z.array(CreateApPaymentRunAllocationSchema).min(1),
});
export const ApPaymentRunAllocationSchema = z.object({
    id: z.uuid(),
    runId: z.uuid(),
    billId: z.uuid(),
    vendorId: z.uuid(),
    amount: DecimalLike,
    whtIncomeType: z.string().nullable().optional(),
    whtRate: DecimalLike.nullable().optional(),
    whtAmount: DecimalLike.nullable().optional(),
});
export const ApPaymentRunSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    paymentDate: z.coerce.date(),
    cashAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    note: z.string().nullable().optional(),
    totalGross: DecimalLike,
    totalWht: DecimalLike,
    totalNet: DecimalLike,
    status: ApPaymentRunStatusSchema,
    whtChannel: WhtChannelSchema,
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const ApPaymentRunWithDetailSchema = ApPaymentRunSchema.extend({
    allocations: z.array(ApPaymentRunAllocationSchema),
    approvals: z.array(ApBillApprovalRowSchema).default([]),
});
// ============================================================
// Aging report
// ============================================================
export const ApAgingBucketSchema = z.object({
    notDue: DecimalLike,
    d1_30: DecimalLike,
    d31_60: DecimalLike,
    d61_90: DecimalLike,
    over90: DecimalLike,
});
export const ApAgingVendorRowSchema = z.object({
    vendorId: z.uuid(),
    vendorCode: z.string(),
    vendorName: z.string(),
    notDue: DecimalLike,
    d1_30: DecimalLike,
    d31_60: DecimalLike,
    d61_90: DecimalLike,
    over90: DecimalLike,
    total: DecimalLike,
});
export const ApAgingResponseSchema = z.object({
    asOfDate: z.coerce.date(),
    totals: ApAgingBucketSchema,
    grandTotal: DecimalLike,
    vendors: z.array(ApAgingVendorRowSchema),
});
// ============================================================
// Duplicate-check (Phase A5) — read-only surfacing of likely duplicate bills
// at entry time, over the dedup keys + vendorInvoiceNo. Hard guard remains the
// server-side P2002→409 in createBill.
// ============================================================
export const CheckDuplicateApBillQuerySchema = z.object({
    vendorId: z.uuid(),
    invoiceNo: z.string().min(1),
    // optional satang amount to tighten the match (vendor+invoice is the primary key).
    totalSatang: z.coerce.number().int().nonnegative().optional(),
});
export const ApBillDuplicateMatchSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    billDate: z.coerce.date(),
    totalAmount: DecimalLike,
    status: ApBillStatusSchema,
});
export const ApBillDuplicateResponseSchema = z.object({
    matches: z.array(ApBillDuplicateMatchSchema),
});
// ============================================================
// B3: Realize deferred input VAT — PATCH .../ap/bills/:id/realize-vat
//
// Captures the original tax invoice that unlocks the deferred (SUSPENSE)
// input-VAT booking. taxInvoiceDate / originalReceivedAt are wire-as-ISO date
// strings (service converts to Date), matching the rest of the AP module.
// ============================================================
export const RealizeApBillVatSchema = z.object({
    taxInvoiceNo: z.string().min(1),
    taxInvoiceDate: z.iso.date(),
    originalReceivedAt: z.iso.date(),
});
// ============================================================
// AP Credit/Debit Note (ใบลดหนี้ / ใบเพิ่มหนี้) — AP Phase C2
//
// Mirrors the AR credit note flipped to the AP/payable side, with a noteType
// discriminator (CREDIT reduces the payable, DEBIT increases it). Always linked
// to an original POSTED ApBill; VAT-adjusted (input VAT); driven through the
// central 4-eyes approval engine (Design-Z: approvalStatus separate from the
// accounting `status`).
// ============================================================
// CREDIT (ใบลดหนี้) | DEBIT (ใบเพิ่มหนี้).
export const CreditDebitTypeSchema = z.enum(['CREDIT', 'DEBIT']);
// Accounting status. The engine walk lives in approvalStatus (Design-Z); this
// status is reflected only at terminals (APPROVED→POSTED, REJECTED→VOID).
export const ApNoteStatusSchema = z.enum(['DRAFT', 'POSTED', 'VOID']);
// Standard credit/debit-note reason codes (editable label in the UI). The
// free-text reasonNote captures specifics; reasonCode drives reporting/RD typing.
export const AP_CREDIT_DEBIT_NOTE_REASONS = {
    price_reduction: 'ลดราคา',
    discount: 'ส่วนลด',
    damaged: 'สินค้าชำรุด',
    short_ship: 'รับไม่ครบ',
    return: 'ส่งคืนสินค้า',
    over_billing: 'ผู้ขายเรียกเก็บเกิน',
    under_billing: 'ผู้ขายเรียกเก็บขาด',
    extra_charge: 'ค่าใช้จ่ายเพิ่มเติม',
    cancel: 'ยกเลิกรายการ',
};
// ------------------------------------------------------------
// Lines — child rows of an ApCreditDebitNote
// ------------------------------------------------------------
export const ApCreditDebitNoteLineSchema = z.object({
    id: z.uuid(),
    noteId: z.uuid(),
    lineNo: z.number().int().positive(),
    description: z.string().min(1),
    qty: DecimalLike,
    unitPrice: DecimalLike,
    amount: DecimalLike,
    expenseCode: z.string().min(1),
});
// Wire shape — id/noteId/lineNo/amount are server-derived.
export const CreateApCreditDebitNoteLineSchema = z.object({
    description: z.string().min(1),
    qty: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    expenseCode: z.string().min(1),
});
// ------------------------------------------------------------
// Header
// ------------------------------------------------------------
// Approval-chain row mirroring ap_credit_debit_note_approvals (maker → reviewer(s)
// → approver). Surfaced on the detail so the web ApprovalTimeline can render it.
export const ApCreditDebitNoteApprovalRowSchema = z.object({
    id: z.uuid(),
    stepNo: z.number().int().positive(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
});
export const ApCreditDebitNoteSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    noteType: CreditDebitTypeSchema,
    noteDate: z.coerce.date(),
    billId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    reasonCode: z.string().min(1),
    reasonNote: z.string().nullable().optional(),
    subtotal: DecimalLike,
    vatRate: DecimalLike,
    vatAmount: DecimalLike,
    totalAmount: DecimalLike,
    currencyCode: z.string().default('THB'),
    status: ApNoteStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Engine-approval columns (kept SEPARATE from the accounting `status`).
    // Null/0 for a note that has not entered the engine.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
});
// List-row shape — adds display names + the source bill's voucher number.
export const ApCreditDebitNoteListRowSchema = ApCreditDebitNoteSchema.extend({
    vendorName: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    billVoucherNo: z.string().nullable().optional(),
});
// Detail shape — includes lines + the approval chain + display names.
export const ApCreditDebitNoteWithLinesSchema = ApCreditDebitNoteSchema.extend({
    lines: z.array(ApCreditDebitNoteLineSchema),
    approvals: z.array(ApCreditDebitNoteApprovalRowSchema).default([]),
    vendorName: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    billVoucherNo: z.string().nullable().optional(),
});
// Wire shape for POST — voucherNo / subtotal / vatAmount / totalAmount are all
// generated server-side. Dates are wire-as-ISO per the AP-module convention.
// vatRate is optional: when omitted the service inherits the source bill's rate.
export const CreateApCreditDebitNoteSchema = z.object({
    noteType: CreditDebitTypeSchema,
    noteDate: z.iso.date(),
    billId: z.uuid(),
    reasonCode: z.string().min(1),
    reasonNote: z.string().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    vatRate: z.coerce.number().min(0).max(100).optional(),
    lines: z.array(CreateApCreditDebitNoteLineSchema).min(1),
});
// ============================================================
// Vendor 360 / statement (AP Phase C5) — a per-vendor read-only aggregation
// over the vendor's AP data (bills / payments / aging / 50-ทวิ certs /
// credit-debit notes) + headline KPIs. Mirrors the AR customer-statement
// pattern but flipped to the AP/vendor side; JSON-only (on-screen + xlsx, no
// PDF). A statement is a bill-level read — REUSES the company-scoped AP read
// surface, no new permission key.
// ============================================================
// Query params for the statement endpoint. `asOf` (wire-as-YYYY-MM-DD) drives
// the aging bucketing + the overdue KPI; defaults to today when omitted.
export const VendorStatementQuerySchema = z.object({
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// Vendor master header — the identity block at the top of the 360 page.
export const VendorStatementHeaderSchema = z.object({
    vendorId: z.uuid(),
    code: z.string(),
    nameTh: z.string(),
    taxId: z.string().nullable().optional(),
    terms: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    addressTh: z.string().nullable().optional(),
    defaultPaymentTermCode: z.string().nullable().optional(),
});
// Headline KPIs. billed = Σ non-VOID bill totals; paid = Σ payment netPaid;
// outstanding = Σ (total + noteAdjustment − paid) over non-VOID/PENDING bills
// (clamped ≥0 per bill); overdue = the slice of outstanding past due as-of;
// dpo ≈ average credit term (dueDate − billDate) over settled (PAID) bills,
// in days (1 dp); null when no PAID bill exists yet.
export const VendorStatementKpisSchema = z.object({
    billed: DecimalLike,
    paid: DecimalLike,
    outstanding: DecimalLike,
    overdue: DecimalLike,
    dpo: DecimalLike.nullable().optional(),
});
// One bill row on the statement — outstanding is the canonical note-adjusted
// balance (total + noteAdjustment − paid).
export const VendorStatementBillSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    billDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    vendorInvoiceNo: z.string().nullable().optional(),
    status: ApBillStatusSchema,
    totalAmount: DecimalLike,
    paidAmount: DecimalLike,
    noteAdjustment: SignedDecimalLike.default(0),
    outstanding: SignedDecimalLike,
    agingDays: z.number().int(),
});
// One payment row — netPaid is the cash-out (gross − wht − discount).
export const VendorStatementPaymentSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    paymentDate: z.coerce.date(),
    totalAmount: DecimalLike,
    whtTotal: DecimalLike.optional(),
    netPaid: DecimalLike.optional(),
    paymentMethod: z.string().nullable().optional(),
});
// One 50-ทวิ cert row — channel lets the page hide print + show the e-WHT
// label for bank-issued certs (mirrors the bill drawer / wht cert-list).
export const VendorStatementCertSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    certDate: z.coerce.date(),
    incomeType: z.string(),
    base: DecimalLike,
    rate: DecimalLike,
    amount: DecimalLike,
    channel: WhtChannelSchema,
});
// One credit/debit note row for the vendor's bills.
export const VendorStatementNoteSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    noteType: CreditDebitTypeSchema,
    noteDate: z.coerce.date(),
    billVoucherNo: z.string().nullable().optional(),
    totalAmount: DecimalLike,
    status: ApNoteStatusSchema,
});
// The full vendor-360 DTO — header + KPIs + the five sections + the aging
// buckets (re-uses the AP aging bucket shape) as-of the statement date.
export const VendorStatementSchema = z.object({
    vendor: VendorStatementHeaderSchema,
    asOf: z.string(),
    generatedAt: z.union([z.string(), z.date()]),
    kpis: VendorStatementKpisSchema,
    bills: z.array(VendorStatementBillSchema),
    payments: z.array(VendorStatementPaymentSchema),
    aging: ApAgingBucketSchema,
    certs: z.array(VendorStatementCertSchema),
    notes: z.array(VendorStatementNoteSchema),
});
// ============================================================
// AP Phase C4 — Recurring vendor bills (template + cron → PENDING bills).
//
// A reusable TEMPLATE drives a daily cron that materializes ONE PENDING ApBill
// per due period via createBill(postJv=false). The big use case is 209-branch
// rent/utilities. Dates are wire-as-YYYY-MM-DD per the AP-module convention.
// ============================================================
export const RecurringFrequencySchema = z.enum(['MONTHLY', 'QUARTERLY', 'YEARLY']);
// ------------------------------------------------------------
// Template lines — child rows. id/templateId/lineNo are server-derived.
// ------------------------------------------------------------
export const RecurringBillTemplateLineSchema = z.object({
    id: z.uuid(),
    templateId: z.uuid(),
    lineNo: z.number().int().positive(),
    description: z.string().min(1),
    qty: DecimalLike,
    unitPrice: DecimalLike,
    expenseCode: z.string().min(1),
});
export const CreateRecurringBillTemplateLineSchema = z.object({
    description: z.string().min(1),
    qty: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    expenseCode: z.string().min(1),
});
// ------------------------------------------------------------
// Template header (read shape).
// ------------------------------------------------------------
export const RecurringBillTemplateSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    description: z.string().min(1),
    vatRate: DecimalLike,
    frequency: RecurringFrequencySchema,
    dayOfMonth: z.coerce.number().int(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date().nullable().optional(),
    nextRunDate: z.coerce.date(),
    paymentTermCode: z.string().nullable().optional(),
    isActive: z.boolean(),
    createdBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List/detail shape — adds lines + display names.
export const RecurringBillTemplateWithLinesSchema = RecurringBillTemplateSchema.extend({
    lines: z.array(RecurringBillTemplateLineSchema),
    vendorName: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
});
// Wire shape for POST — id/nextRunDate/createdBy are server-derived; nextRunDate
// is seeded from startDate + dayOfMonth at create time.
export const CreateRecurringBillTemplateSchema = z.object({
    vendorId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    description: z.string().min(1),
    vatRate: z.coerce.number().min(0).max(100).default(7),
    frequency: RecurringFrequencySchema.default('MONTHLY'),
    dayOfMonth: z.coerce.number().int().min(1).max(28).default(1),
    startDate: z.iso.date(),
    endDate: z.iso.date().nullable().optional(),
    paymentTermCode: z.string().optional(),
    lines: z.array(CreateRecurringBillTemplateLineSchema).min(1),
});
// Wire shape for PATCH — every field optional; lines (when present) REPLACE the
// template's line set. dayOfMonth/frequency/startDate changes do NOT retro-shift
// an already-advanced nextRunDate (only future generations follow the new cadence).
export const UpdateRecurringBillTemplateSchema = z.object({
    description: z.string().min(1).optional(),
    vatRate: z.coerce.number().min(0).max(100).optional(),
    frequency: RecurringFrequencySchema.optional(),
    dayOfMonth: z.coerce.number().int().min(1).max(28).optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    endDate: z.iso.date().nullable().optional(),
    paymentTermCode: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
    lines: z.array(CreateRecurringBillTemplateLineSchema).min(1).optional(),
});
// Manual-trigger body for POST .../ap/recurring-bills/generate. Both optional:
// asOf (wire-as-YYYY-MM-DD) defaults to today; omitting companyId runs across
// every company (the cron's all-companies sweep).
export const GenerateRecurringBillsSchema = z.object({
    asOf: z.iso.date().optional(),
    companyId: z.uuid().optional(),
});
// Summary returned by generateDue / the manual endpoint.
export const RecurringBillsGenerateResultSchema = z.object({
    generated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
// ============================================================
// AP KPI dashboard (AP Phase C7a) — a READ-ONLY company-wide aggregation over
// the AP module's bills + payments. Mirrors the vendor-statement DPO approach
// (avg credit term over PAID bills) but at the company level, and adds the
// process KPIs (on-time payment %, discount-capture %, exception/touchless %).
// JSON-only; no schema change, no writes. `asOf` (wire-as-YYYY-MM-DD) drives the
// aging bucketing + overdue slice and defaults to today server-side.
// ============================================================
// Query params for the dashboard endpoint.
export const ApDashboardQuerySchema = z.object({
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// One top-vendor-by-outstanding row.
export const ApDashboardTopVendorSchema = z.object({
    vendorId: z.uuid(),
    name: z.string(),
    outstanding: DecimalLike,
});
// One monthly-trend point — billed vs paid for the period (YYYYMM).
export const ApDashboardTrendPointSchema = z.object({
    period: z.string(), // YYYYMM
    billed: DecimalLike,
    paid: DecimalLike,
});
// The full dashboard DTO. Money KPIs (string 2dp); the % / day KPIs are numbers
// (1 dp) or null when undefined (no data). See ApDashboardService for the exact
// definitions of dpo / onTimePaymentPct / discountCapturePct / exceptionRate.
export const ApDashboardSchema = z.object({
    asOf: z.string(),
    generatedAt: z.union([z.string(), z.date()]),
    // Money headline KPIs (exclude VOID/PENDING from outstanding/overdue).
    billedTotal: DecimalLike,
    paidTotal: DecimalLike,
    outstandingTotal: DecimalLike,
    overdueTotal: DecimalLike,
    // dpo ≈ avg credit term (dueDate − billDate) over PAID bills, in days (1 dp);
    // null when no PAID bill exists.
    dpo: z.number().nullable(),
    // % of payments settled on or before the max dueDate of their allocated bills;
    // null when there are no payments with allocations.
    onTimePaymentPct: z.number().nullable(),
    // captured ÷ (captured + missed-available) early-payment discount; null when 0.
    discountCapturePct: z.number().nullable(),
    // engine-managed bills ÷ total bills (APPROXIMATION of straight-through-vs-exception).
    exceptionRate: z.number().nullable(),
    touchlessPct: z.number().nullable(),
    aging: ApAgingBucketSchema,
    topVendors: z.array(ApDashboardTopVendorSchema),
    monthlyTrend: z.array(ApDashboardTrendPointSchema),
});
//# sourceMappingURL=ap.js.map